/**
 * SW4P Storefront API Service
 * 
 * Production API client for cross-chain token purchases.
 * Implements the full relay flow: prepare → permit → relay → poll.
 */

const API_URL = '/api';

// =============================================================================
// Helpers
// =============================================================================

/**
 * Extract error message from API response (consistent pattern)
 */
async function extractErrorMessage(response: Response, fallback: string): Promise<string> {
    const text = await response.text();
    if (!text) return fallback;

    try {
        const json = JSON.parse(text);
        return json.detail || json.error || json.message || text;
    } catch {
        return text;
    }
}

// =============================================================================
// Types
// =============================================================================

export interface PermitData {
    owner: string;
    spender: string;
    value: string;
    nonce: string;
    deadline: string;
    v: number;
    r: string;
    s: string;
}

export interface PrepareRelayRequest {
    token_in: string;
    amount_in: string;
    source_domain: number;
    destination_domain: number;
    mint_recipient: string;
    recipient_wallet: string;
    max_fee?: string;
    token_out?: string;
}

export interface PrepareRelayResponse {
    intent_id: string;
    status: string;
}

export interface RelayRequest {
    intent_id?: string;
    token_in: string;
    amount_in: string;
    source_domain: number;
    destination_domain: number;
    mint_recipient: string;
    recipient_wallet: string;
    permit_data?: PermitData;
    idempotency_key?: string;
    max_fee?: string;
    min_finality_threshold?: number;
    token_out?: string;
    mev_protection?: boolean;
}

export interface RelayResponse {
    tx_request_id: string;
    status: string;
}

export type TransactionStatusValue =
    | 'PREPARING'
    | 'PENDING'
    | 'DETECTED'
    | 'ATTESTED'
    | 'MINTED'
    | 'SWAPPED'
    | 'COMPLETED'
    | 'FAILED';

export interface TransactionStatus {
    tx_id: string;
    status: TransactionStatusValue;
    source_chain: string;
    amount_usdc: number;
    failure_reason?: string;
    tx_hash?: string;
    swap_tx_hash?: string;
}

export interface WaaSWallet {
    wallet_id: string;
    address: string;
    blockchain: string;
}

export interface ProjectWallet {
    id: string;
    project_id: string;
    user_wallet: string;
    waas_wallet_id: string;
    waas_address: string;
}

export interface PermitNonceResponse {
    nonce: string;
    expires_at: number;
}

export interface ValidateNonceResponse {
    valid: boolean;
    current_nonce: string;
    message?: string;
}

export interface FeeEstimate {
    total_fee_usdc: number;
    total_to_charge_usdc: number;
    net_amount_usdc: number;
    fee_percent: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get or create project-scoped WaaS wallet for the authenticated user
 */
export async function getProjectWallet(projectId: string, authToken: string): Promise<ProjectWallet> {
    const response = await fetch(`${API_URL}/v1/project/wallet`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ project_id: projectId }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to get project wallet');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Get legacy WaaS wallet (fallback for non-project flows)
 */
export async function getOrCreateSolanaWallet(authToken: string): Promise<WaaSWallet> {
    const response = await fetch(`${API_URL}/v1/waas/solana-wallet`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to get wallet');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Ensure USDC ATA exists for a Solana wallet (required before CCTP mint)
 */
export async function ensureUsdcAta(walletAddress: string, authToken: string): Promise<{ ata_address: string; created: boolean }> {
    const response = await fetch(`${API_URL}/v1/waas/ensure-usdc-ata`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ wallet_address: walletAddress }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to ensure USDC ATA');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Record transaction intent BEFORE user signs permit (prevents orphaned mints)
 */
export async function prepareRelay(request: PrepareRelayRequest, authToken: string): Promise<PrepareRelayResponse> {
    const response = await fetch(`${API_URL}/v1/relay/prepare`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to prepare relay');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Get permit nonce for USDC transfer
 */
export async function getPermitNonce(chainCode: string, owner: string, authToken: string): Promise<PermitNonceResponse> {
    const response = await fetch(`${API_URL}/v1/permit/nonce`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ chain: chainCode, owner }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to fetch nonce');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Validate nonce is still current before signing
 */
export async function validateNonce(
    chainCode: string,
    owner: string,
    expectedNonce: string,
    authToken: string
): Promise<ValidateNonceResponse> {
    const response = await fetch(`${API_URL}/v1/permit/validate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ chain: chainCode, owner, expected_nonce: expectedNonce }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to validate nonce');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Submit gasless relay transaction
 */
export async function relayTransaction(request: RelayRequest, authToken: string): Promise<RelayResponse> {
    const response = await fetch(`${API_URL}/v1/tx/relay`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to relay transaction');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Poll transaction status
 */
export async function getTransactionStatus(txId: string, authToken: string): Promise<TransactionStatus> {
    const response = await fetch(`${API_URL}/v1/tx/status`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ tx_id: txId }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to check status');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Fetch CCTP bridge fee from Circle Iris API
 */
export async function fetchBridgeFee(
    amount: string,
    sourceDomain: number,
    destDomain: number
): Promise<{ maxFee: string; minFinalityThreshold: number }> {
    const MIN_FAST_FEE = '0.05';
    const MIN_FINALITY = 1000;

    try {
        const url = `https://iris-api.circle.com/v2/burn/USDC/fees/${sourceDomain}/${destDomain}`;
        const response = await fetch(url);
        if (!response.ok) return { maxFee: MIN_FAST_FEE, minFinalityThreshold: MIN_FINALITY };

        const tiers = await response.json();
        const fastTier = tiers.find((t: any) => t.finalityThreshold <= 1000);

        if (fastTier) {
            const feeBps = fastTier.minimumFee;
            const amountVal = parseFloat(amount);
            const feeAmount = (amountVal * feeBps) / 10000;
            const minFee = Math.max(feeAmount, 0.05);
            return { maxFee: minFee.toFixed(6), minFinalityThreshold: fastTier.finalityThreshold };
        }

        return { maxFee: MIN_FAST_FEE, minFinalityThreshold: MIN_FINALITY };
    } catch {
        return { maxFee: MIN_FAST_FEE, minFinalityThreshold: MIN_FINALITY };
    }
}

/**
 * Get fee estimate for a swap
 */
export async function fetchFeeEstimate(
    amount: string,
    sourceChain: string,
    destChain: string,
    tokenIn: string
): Promise<FeeEstimate> {
    const response = await fetch(`${API_URL}/v1/fees/estimate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            amount_usdc: parseFloat(amount),
            source_chain: sourceChain,
            dest_chain: destChain,
            token_in: tokenIn,
        }),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch fee estimate');
    }

    return response.json();
}

// =============================================================================
// Auth
// =============================================================================

export interface AuthNonceResponse {
    nonce: string;
    expires_at: number;
}

export interface AuthLoginResponse {
    token: string;
    expires_at: number;
}

/**
 * Request a nonce for wallet authentication
 */
export async function requestAuthNonce(walletAddress: string, chain: string): Promise<AuthNonceResponse> {
    const response = await fetch(`${API_URL}/v1/auth/nonce`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, chain }),
    });

    if (!response.ok) {
        throw new Error('Failed to get auth nonce');
    }

    return response.json();
}

/**
 * Login with signed message to get JWT token
 */
export async function authLogin(walletAddress: string, chain: string, signature: string): Promise<AuthLoginResponse> {
    const response = await fetch(`${API_URL}/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallet_address: walletAddress, chain, signature }),
    });

    if (!response.ok) {
        throw new Error('Authentication failed');
    }

    return response.json();
}

// =============================================================================
// Cross-Chain Intent (Solver Auction / Dual-Mode)
// =============================================================================

export type IntentStateValue =
    | 'SUBMITTED'
    | 'QUOTING'
    | 'QUOTED'
    | 'FILLING'
    | 'FILLED'
    | 'SETTLING'
    | 'SETTLED'
    | 'COMPLETED'
    | 'EXPIRED'
    | 'FAILED'
    | 'CANCELLED';

export interface IntentQuote {
    intentId: string;
    estimatedOutput: number;
    feeBreakdown: {
        platformFee: number;
        bridgeFee: number;
        gasFee: number;
        totalFee: number;
    };
    estimatedTimeSeconds: number;
    routeDescription: string;
    expiresAt: number;
}

export interface IntentStatusResponse {
    intentId: string;
    state: IntentStateValue;
    quote?: IntentQuote;
    fillTxHash?: string;
    settlementTxHash?: string;
    outputAmount?: number;
    createdAt: number;
    updatedAt: number;
}

export interface SubmitIntentParams {
    source_chain: string;
    dest_chain: string;
    input_token: string;
    output_token: string;
    input_amount: number;
    min_output_amount?: number;
    max_slippage_bps?: number;
    deadline: number;
    recipient: string;
    sender: string;
    webhook_url?: string;
    idempotency_key?: string;
    mev_protection?: boolean;
}

export interface RouteConfigResponse {
    chains: string[];
    tokens: string[];
    features: Record<string, boolean>;
    mev?: {
        featureEnabled: boolean;
        requestToggleAvailable: boolean;
        executionIntegrated: boolean;
        status: 'live' | 'staged';
        evmReady: boolean;
        solanaReady: boolean;
        plannedStrategies?: {
            evm?: string;
            solana?: string;
        };
        note?: string;
    };
}

export async function getRouteConfig(): Promise<RouteConfigResponse> {
    const response = await fetch(`${API_URL}/v1/config/routes`, {
        method: 'GET',
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to fetch route config');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Submit a cross-chain intent for solver auction (dual-mode).
 * Returns a quote immediately; if solver auction is enabled, an auction opens
 * and external fillers compete to fill within ~10s.
 */
export async function submitCrossChainIntent(
    params: SubmitIntentParams,
    authToken: string
): Promise<IntentQuote> {
    const response = await fetch(`${API_URL}/sdk/v2/intent`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
            sourceChain: params.source_chain,
            destChain: params.dest_chain,
            inputToken: params.input_token,
            outputToken: params.output_token,
            inputAmount: params.input_amount,
            minOutputAmount: params.min_output_amount,
            maxSlippageBps: params.max_slippage_bps,
            mevProtection: params.mev_protection,
            deadline: params.deadline,
            recipient: params.recipient,
            sender: params.sender,
            webhookUrl: params.webhook_url,
            idempotencyKey: params.idempotency_key,
        }),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to submit intent');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Get the current status of a cross-chain intent.
 * Poll this to track solver fill progress.
 */
export async function getIntentStatus(
    intentId: string,
    authToken: string
): Promise<IntentStatusResponse> {
    const response = await fetch(`${API_URL}/sdk/v2/intent/${intentId}`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${authToken}`,
        },
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to get intent status');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Cancel a cross-chain intent (only if not yet filling).
 */
export async function cancelIntent(
    intentId: string,
    authToken: string
): Promise<{ intentId: string; state: string; message: string }> {
    const response = await fetch(`${API_URL}/sdk/v2/intent/${intentId}/cancel`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${authToken}`,
        },
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Failed to cancel intent');
        throw new Error(msg);
    }

    return response.json();
}

// =============================================================================
// Jupiter Quote
// =============================================================================

export interface JupiterQuoteParams {
    inputMint: string;
    outputMint: string;
    amount: string;
    slippageBps?: number;
}

export interface JupiterQuote {
    inputMint: string;
    outputMint: string;
    inAmount: string;
    outAmount: string;
    priceImpactPct: string;
}

/**
 * Get Jupiter swap quote for token output estimation
 */
export async function getJupiterQuote(params: JupiterQuoteParams): Promise<JupiterQuote | null> {
    try {
        const url = new URL('https://api.jup.ag/swap/v1/quote');
        url.searchParams.set('inputMint', params.inputMint);
        url.searchParams.set('outputMint', params.outputMint);
        url.searchParams.set('amount', params.amount);
        url.searchParams.set('slippageBps', String(params.slippageBps || 100));

        const response = await fetch(url.toString());
        if (!response.ok) return null;

        return response.json();
    } catch {
        return null;
    }
}

// =============================================================================
// Withdrawal
// =============================================================================

export interface WithdrawRequest {
    amount: string;
    dest_chain: string;
    dest_address: string;
    swap_555?: boolean;
}

export interface WithdrawResponse {
    tx_id: string;
    status: string;
    message?: string;
}

export interface WithdrawFeeEstimate {
    platform_fee: number;
    total_fees: number;
    net_receive: number;
    fee_tier: string;
}

/**
 * Initiate a withdrawal from WaaS wallet to user's external wallet
 */
export async function withdrawFunds(request: WithdrawRequest, authToken: string): Promise<WithdrawResponse> {
    const response = await fetch(`${API_URL}/v1/tx/withdraw`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        const msg = await extractErrorMessage(response, 'Withdrawal failed');
        throw new Error(msg);
    }

    return response.json();
}

/**
 * Get fee estimate for a withdrawal
 */
export async function getWithdrawFeeEstimate(request: WithdrawRequest, authToken: string): Promise<WithdrawFeeEstimate> {
    const response = await fetch(`${API_URL}/v1/tx/withdraw/estimate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(request),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch withdrawal fee estimate');
    }

    return response.json();
}

// =============================================================================
// Wallet Balances
// =============================================================================

export interface TokenBalance {
    symbol: string;
    amount: string;
    usd_value: number;
}

export interface WalletBalancesResponse {
    tokens: TokenBalance[];
}

/**
 * Fetch balances for a wallet address
 */
export async function getWalletBalances(walletAddress: string, authToken: string): Promise<WalletBalancesResponse> {
    const response = await fetch(`${API_URL}/v1/wallet/balances`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ wallet_address: walletAddress }),
    });

    if (!response.ok) {
        throw new Error('Failed to fetch wallet balances');
    }

    return response.json();
}
